import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Building2, RefreshCw, Power, PowerOff, Users, AlertTriangle, Shield,
  Activity, DollarSign, Mail, FileCheck, Zap, Eye,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { PageHeader, MetricRow, FilterBar, DataTable } from '../design';

const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

export default function Organizations() {
  const { isAuthenticated } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [actionDialog, setActionDialog] = useState({ open: false, action: '', org: null });
  const [actionReason, setActionReason] = useState('');
  const [bulkDialog, setBulkDialog] = useState({ open: false, action: '', orgs: [], clear: null });
  const [bulkReason, setBulkReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [budgetRiskFilter, setBudgetRiskFilter] = useState('all');
  const [pausedAgentsFilter, setPausedAgentsFilter] = useState('all');

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getOrganizationsEnhanced();
      setOrgs(res.data || []);
    } catch (error) {
      try {
        const fallback = await governanceAPI.getOrganizations();
        setOrgs(fallback.data || []);
      } catch (e) {
        toast.error('Failed to load organizations');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAuthenticated) fetchOrgs(); }, [isAuthenticated, fetchOrgs]);

  const filteredOrgs = useMemo(() => orgs.filter((org) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!org.name?.toLowerCase().includes(q) && !org.org_id?.toLowerCase().includes(q) && !org.owner_email?.toLowerCase().includes(q)) return false;
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'enabled' && org.kill_switch_active) return false;
      if (statusFilter === 'paused' && !org.kill_switch_active) return false;
      if (statusFilter === 'suspended' && org.status !== 'suspended') return false;
    }
    if (budgetRiskFilter !== 'all') {
      if (budgetRiskFilter === 'at-risk' && org.budget_risk_count === 0) return false;
      if (budgetRiskFilter === 'healthy' && org.budget_risk_count > 0) return false;
    }
    if (pausedAgentsFilter !== 'all') {
      if (pausedAgentsFilter === 'has-paused' && org.auto_paused_agents === 0) return false;
      if (pausedAgentsFilter === 'all-active' && org.auto_paused_agents > 0) return false;
    }
    return true;
  }), [orgs, searchQuery, statusFilter, budgetRiskFilter, pausedAgentsFilter]);

  const agg = useMemo(() => ({
    totalOrgs: orgs.length,
    totalTenants: orgs.reduce((s, o) => s + (o.active_tenants || 0) + (o.paused_tenants || 0), 0),
    totalUsers: orgs.reduce((s, o) => s + (o.total_users || 0), 0),
    totalAiExecutions: orgs.reduce((s, o) => s + (o.ai_executions_30d || 0), 0),
    totalCost: orgs.reduce((s, o) => s + (o.ai_cost_mtd || 0), 0),
    budgetRisks: orgs.reduce((s, o) => s + (o.budget_risk_count || 0), 0),
    pendingApprovals: orgs.reduce((s, o) => s + (o.pending_approvals || 0), 0),
  }), [orgs]);

  const executeAction = async (action, orgId, reason) => {
    try {
      const res = await governanceAPI.executeOrgAction(orgId, { action, config: { reason: reason || 'SuperAdmin action' } });
      toast.success(res.data.summary || 'Action completed');
      fetchOrgs();
    } catch (error) {
      toast.error('Error executing action');
    }
  };

  const openActionDialog = (action, org) => { setActionDialog({ open: true, action, org }); setActionReason(''); };
  const confirmAction = () => { executeAction(actionDialog.action, actionDialog.org.org_id, actionReason); setActionDialog({ open: false, action: '', org: null }); };

  const openBulkDialog = (action, orgs, clear) => { setBulkDialog({ open: true, action, orgs, clear }); setBulkReason(''); };
  const confirmBulkAction = async () => {
    const { action, orgs, clear } = bulkDialog;
    setBulkDialog({ open: false, action: '', orgs: [], clear: null });
    let ok = 0, fail = 0;
    for (const org of orgs) {
      try {
        await governanceAPI.executeOrgAction(org.org_id, { action, config: { reason: bulkReason || 'Bulk SuperAdmin action' } });
        ok += 1;
      } catch (e) { fail += 1; }
    }
    if (ok) toast.success(`${action === 'ORG_KILL_SWITCH_ON' ? 'Paused' : 'Enabled'} AI for ${ok} org${ok > 1 ? 's' : ''}${fail ? ` · ${fail} failed` : ''}`);
    else toast.error('Bulk action failed');
    if (clear) clear();
    fetchOrgs();
  };

  const metrics = [
    { key: 'orgs', label: 'Organizations', value: agg.totalOrgs, icon: Building2 },
    { key: 'users', label: 'Total Users', value: agg.totalUsers, icon: Users, tone: 'accent' },
    { key: 'ai-exec', label: 'AI Exec (30d)', value: agg.totalAiExecutions, icon: Zap, tone: 'accent' },
    { key: 'cost', label: 'AI Cost (MTD)', value: formatCurrency(agg.totalCost), icon: DollarSign, tone: 'success' },
    { key: 'budget-risk', label: 'Budget Risks', value: agg.budgetRisks, icon: AlertTriangle, tone: agg.budgetRisks > 0 ? 'warning' : 'muted' },
    { key: 'approvals', label: 'Pending Approvals', value: agg.pendingApprovals, icon: FileCheck, tone: agg.pendingApprovals > 0 ? 'warning' : 'muted' },
    { key: 'tenants', label: 'Total Tenants', value: agg.totalTenants, icon: Shield, tone: 'muted' },
  ];

  const columns = [
    { accessorKey: 'name', header: 'Organization', cell: ({ row }) => {
      const org = row.original;
      return (
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${org.kill_switch_active ? 'bg-rose-500/15' : 'bg-[var(--gov-bg-elevated)]'}`}>
            <Building2 className={`h-4.5 w-4.5 ${org.kill_switch_active ? 'text-rose-400' : 'gov-text-secondary'}`} />
          </div>
          <div>
            <div className="font-medium gov-text-primary">{org.name}</div>
            <div className="text-xs gov-text-tertiary flex items-center gap-2">
              <span className="font-mono">{org.org_id?.substring(0, 8)}…</span>
              {org.owner_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{org.owner_email.split('@')[0]}</span>}
            </div>
          </div>
        </div>
      );
    } },
    { accessorKey: 'kill_switch_active', header: 'Status', cell: ({ row }) => {
      const org = row.original;
      return (
        <div className="space-y-1">
          {org.kill_switch_active ? <Badge variant="destructive">AI PAUSED</Badge>
            : org.status === 'suspended' ? <Badge variant="secondary">SUSPENDED</Badge>
            : <Badge className="bg-emerald-600 hover:bg-emerald-600">AI ENABLED</Badge>}
          {org.plan_type && org.plan_type !== 'unknown' && <div className="text-xs gov-text-tertiary capitalize">{org.plan_type}</div>}
        </div>
      );
    } },
    { accessorKey: 'total_users', header: 'Users', cell: ({ row }) => (
      <div>
        <span className="flex items-center gap-2 font-medium gov-text-primary"><Users className="h-4 w-4 gov-text-tertiary" />{row.original.total_users || 0}</span>
        <div className="text-xs gov-text-tertiary">{row.original.admin_users || 0} admins</div>
      </div>
    ) },
    { accessorKey: 'ai_executions_30d', header: 'AI Usage (30d)', cell: ({ row }) => (
      <div>
        <span className="flex items-center gap-2 font-medium gov-text-primary"><Activity className="h-4 w-4 text-purple-400" />{row.original.ai_executions_30d || 0}</span>
        {row.original.most_used_agent && <div className="text-xs gov-text-tertiary capitalize">Top: {row.original.most_used_agent}</div>}
      </div>
    ) },
    { accessorKey: 'ai_cost_mtd', header: 'Cost (MTD)', cell: ({ row }) => (
      <div>
        <span className="flex items-center gap-2 font-medium gov-text-primary"><DollarSign className="h-4 w-4 text-emerald-400" />{formatCurrency(row.original.ai_cost_mtd)}</span>
        {row.original.budget_risk_count > 0 && <div className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{row.original.budget_risk_count} at risk</div>}
      </div>
    ) },
    { accessorKey: 'approval_rate', header: 'Approvals', cell: ({ row }) => (
      <div className="space-y-1">
        <span className="font-medium gov-text-primary">{row.original.approval_rate || 0}% <span className="text-xs gov-text-tertiary">rate</span></span>
        {row.original.pending_approvals > 0 && <Badge variant="secondary" className="text-xs">{row.original.pending_approvals} pending</Badge>}
      </div>
    ) },
    { id: 'actions', header: '', cell: ({ row }) => {
      const org = row.original;
      return (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {org.kill_switch_active
            ? <Button size="sm" variant="default" onClick={() => openActionDialog('ORG_KILL_SWITCH_OFF', org)} data-testid={`enable-${org.org_id}`}><Power className="h-4 w-4 mr-1" />Enable</Button>
            : <Button size="sm" variant="destructive" onClick={() => openActionDialog('ORG_KILL_SWITCH_ON', org)} data-testid={`pause-${org.org_id}`}><PowerOff className="h-4 w-4 mr-1" />Pause</Button>}
          <Button size="sm" variant="outline" onClick={() => navigate(`/governance/organizations/${org.org_id}`)} data-testid={`view-${org.org_id}`}><Eye className="h-4 w-4 mr-1" />View</Button>
        </div>
      );
    } },
  ];

  return (
    <div className="space-y-8" data-testid="organizations">
      <PageHeader
        title="Organizations"
        subtitle="Enhanced multi-org enterprise control plane"
        actions={
          <Button variant="outline" size="sm" onClick={fetchOrgs} data-testid="refresh-orgs">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        }
      />

      <MetricRow metrics={metrics} cols={7} />

      <FilterBar
        search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search name, ID, owner…' }}
        filters={[
          { key: 'status', label: 'Status', value: statusFilter, onChange: setStatusFilter, options: [
            { value: 'enabled', label: 'AI Enabled' }, { value: 'paused', label: 'AI Paused' }, { value: 'suspended', label: 'Suspended' },
          ], defaultLabel: 'All Status' },
          { key: 'budget', label: 'Budget', value: budgetRiskFilter, onChange: setBudgetRiskFilter, options: [
            { value: 'at-risk', label: 'At Risk' }, { value: 'healthy', label: 'Healthy' },
          ], defaultLabel: 'All Budget' },
          { key: 'agents', label: 'Agents', value: pausedAgentsFilter, onChange: setPausedAgentsFilter, options: [
            { value: 'has-paused', label: 'Has Paused' }, { value: 'all-active', label: 'All Active' },
          ], defaultLabel: 'All Agents' },
        ]}
        resultCount={filteredOrgs.length}
        onReset={() => { setSearchQuery(''); setStatusFilter('all'); setBudgetRiskFilter('all'); setPausedAgentsFilter('all'); }}
      />

      <DataTable
        columns={columns}
        data={filteredOrgs}
        loading={loading}
        testId="organizations-table"
        onRowClick={(o) => navigate(`/governance/organizations/${o.org_id}`)}
        emptyIcon={Building2}
        emptyTitle={orgs.length === 0 ? 'No organizations configured' : 'No organizations match your filters'}
        emptyHint={orgs.length === 0 ? 'Organizations appear here as they are created.' : 'Try adjusting your filters.'}
        pageSize={25}
        bulkActions={(rows, clear) => (
          <>
            <Button size="sm" variant="default" onClick={() => openBulkDialog('ORG_KILL_SWITCH_OFF', rows, clear)} data-testid="bulk-enable-orgs">
              <Power className="h-4 w-4 mr-1" />Enable AI ({rows.length})
            </Button>
            <Button size="sm" variant="destructive" onClick={() => openBulkDialog('ORG_KILL_SWITCH_ON', rows, clear)} data-testid="bulk-pause-orgs">
              <PowerOff className="h-4 w-4 mr-1" />Pause AI ({rows.length})
            </Button>
          </>
        )}
      />

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionDialog.action === 'ORG_KILL_SWITCH_ON' ? 'Pause Organization AI' : 'Enable Organization AI'}</DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'ORG_KILL_SWITCH_ON'
                ? `This will pause AI for all tenants in ${actionDialog.org?.name}. All AI operations will be blocked.`
                : `This will enable AI for ${actionDialog.org?.name}. AI operations will resume for all tenants.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Reason (required for audit)</Label>
            <Textarea id="reason" placeholder="Enter reason for this action…" value={actionReason} onChange={(e) => setActionReason(e.target.value)} className="mt-2" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, action: '', org: null })}>Cancel</Button>
            <Button variant={actionDialog.action === 'ORG_KILL_SWITCH_ON' ? 'destructive' : 'default'} onClick={confirmAction} disabled={!actionReason.trim()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkDialog.open} onOpenChange={(open) => setBulkDialog({ ...bulkDialog, open })}>
        <DialogContent data-testid="bulk-action-dialog">
          <DialogHeader>
            <DialogTitle>{bulkDialog.action === 'ORG_KILL_SWITCH_ON' ? 'Pause AI — Bulk' : 'Enable AI — Bulk'}</DialogTitle>
            <DialogDescription>
              {bulkDialog.action === 'ORG_KILL_SWITCH_ON'
                ? `This will pause AI for ${bulkDialog.orgs.length} selected organization(s). All AI operations will be blocked.`
                : `This will enable AI for ${bulkDialog.orgs.length} selected organization(s). AI operations will resume.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="bulk-reason">Reason (required for audit)</Label>
            <Textarea id="bulk-reason" placeholder="Enter reason for this bulk action…" value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} className="mt-2" data-testid="bulk-action-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog({ open: false, action: '', orgs: [], clear: null })}>Cancel</Button>
            <Button variant={bulkDialog.action === 'ORG_KILL_SWITCH_ON' ? 'destructive' : 'default'} onClick={confirmBulkAction} disabled={!bulkReason.trim()} data-testid="bulk-action-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
